<template>
  <div class="meeting-list-page">
    <!-- Bloc 1: Entête & Navigation -->
    <div class="app-block app-block--header">
      <!-- Icône de l'application + Titre -->
      <div class="app-block-title">
        <FeatherIcon type="calendar" size="24"></FeatherIcon>
        <h1 style="margin-left: 25px">Meeting Hub</h1>
      </div>
      <!-- Futurs éléments: Recherche, filtres, etc. -->
    </div>

    <!-- Bloc 2: Bouton Ajouter Réunion -->
    <div class="add-button-block" style="margin-bottom: 25px">
      <BaseButton variant="danger" @click="goToNewMeeting">
        <FeatherIcon type="plus-circle" size="18"></FeatherIcon>
        Nouvelle Réunion
      </BaseButton>
    </div>

    <!-- Bloc 3: Liste des Réunions -->
    <div class="app-block app-block--list">
      <div class="app-block-title">
        <FeatherIcon type="list" size="24"></FeatherIcon>
        <h2 style="margin-left: 25px">Toutes les Réunions</h2>
      </div>

      <div v-if="loading">Chargement des réunions...</div>
      <div v-else-if="error">Erreur: {{ error }}</div>
      <div v-else-if="meetings.length === 0" class="no-meetings-message">
        Aucune réunion trouvée. Créez-en une pour commencer !
      </div>
      <div v-else class="meeting-cards-list">
        <!-- Boucle sur les réunions -->
        <MeetingCard
          style="margin-bottom: 5px"
          v-for="meeting in meetings"
          :key="meeting.id"
          :meeting="meeting"
          @view="viewMeetingDetail(meeting.id)"
          @edit="editMeeting(meeting.id)"
          @delete="deleteMeeting(meeting.id)"
        ></MeetingCard>
      </div>
    </div>
  </div>
</template>

<script>
import MeetingCard from "@/components/MeetingCard.vue";
import BaseButton from "@/components/BaseButton.vue";
// FeatherIcon est enregistré globalement
import apiService from "@/services/apiService";

export default {
  name: "MeetingListView",
  components: {
    // eslint-disable-next-line vue/no-unused-components
    MeetingCard,
    // eslint-disable-next-line vue/no-unused-components
    BaseButton,
    // FeatherIcon n'est pas listé ici car il est enregistré globalement
  },
  data() {
    return {
      meetings: [],
      loading: false, // chargement de la liste
      error: null, // erreur chargement liste
      deletingMeetingId: null, // Pour suivre quelle réunion est en cours de suppression
    };
  },
  created() {
    this.fetchMeetings();
  },
  methods: {
    async fetchMeetings() {
      this.loading = true;
      this.error = null;
      try {
        const data = await apiService.getMeetings();
        this.meetings = data;
      } catch (e) {
        this.error = "Impossible de charger les réunions: " + e.message;
        console.error("Fetching meetings failed:", e);
      } finally {
        this.loading = false;
      }
    },
    goToNewMeeting() {
      this.$router.push("/new-meeting");
    },
    viewMeetingDetail(id) {
      this.$router.push({ name: "MeetingDetail", params: { id: id } });
    },
    // Naviguer vers la page d'édition
    editMeeting(id) {
      this.$router.push({ name: "EditMeeting", params: { id: id } });
    },
    // Supprimer une réunion
    async deleteMeeting(id) {
      // Demander confirmation avant de supprimer
      if (
        !confirm(
          "Êtes-vous sûr de vouloir supprimer cette réunion ? Cette action est irréversible et supprimera aussi toutes les actions liées."
        )
      ) {
        return; // Annuler si l'utilisateur ne confirme pas
      }

      // Suivre l'ID en cours de suppression pour potentiellement désactiver le bouton (non implémenté visuellement pour l'instant)
      this.deletingMeetingId = id;
      this.error = null;

      try {
        await apiService.deleteMeeting(id);
        console.log(`Réunion ${id} supprimée.`);
        // Supprimer la réunion de la liste locale sans recharger tout
        this.meetings = this.meetings.filter((meeting) => meeting.id !== id);
      } catch (e) {
        this.error =
          `Erreur lors de la suppression de la réunion ${id}: ` + e.message;
        console.error(`Delete meeting ${id} failed:`, e);
        // Afficher un message à l'utilisateur (ex: via un système de notification ou alert)
        alert(this.error);
      } finally {
        this.deletingMeetingId = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
// Fichier: frontend/src/views/MeetingDetailView.vue
// Section <style scoped> - Actions Block (v5 - Simplification CSS)

@import "@/assets/css/_variables.scss"; // Assurez-vous que cet import est bien là

.meeting-detail-page {
  max-width: 900px;
  margin: $space-xl auto;
  padding: 0 $space-md;
}

// Copie des styles de base des blocs pour éviter les problèmes de @extend avec scoped
.app-block {
  background-color: $color-surface;
  padding: $space-lg;
  margin-bottom: $space-lg;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-md;
}

.app-block-title {
  font-family: $font-heading;
  font-size: $font-size-xl;
  color: $color-text-900;
  margin-bottom: $space-lg;
  padding-bottom: $space-md;
  border-bottom: 1px solid $color-primary-100;
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  .feather-icon {
    flex-shrink: 0;
    margin-right: $space-sm;
    color: $color-primary-500;
  }

  h1,
  h2 {
    flex-grow: 1;
    margin: 0;
    padding: 0;
    line-height: 1.2;
  }

  .meeting-actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: $space-sm;
    margin-left: $space-md;

    @media (max-width: $breakpoint-md) {
      margin-top: $space-sm;
      margin-left: 0;
      width: 100%;
      justify-content: flex-end;
    }
  }
}

.message-block {
  text-align: center;
  p {
    margin-bottom: $space-md;
    font-size: $font-size-lg;
  }
  .back-button-container {
    margin-top: $space-md;
  }
}

.meeting-meta {
  font-size: $font-size-sm;
  color: $color-text-600;
  margin-top: $space-sm;
  padding-top: $space-sm;
  border-top: 1px solid $color-background;
  p {
    margin-bottom: $space-xs;
    display: flex;
    align-items: center;
    .feather-icon {
      margin-right: $space-xs;
      color: $color-text-300;
    }
  }
}

.notes-block {
  .notes-content {
    line-height: 1.8;
    color: $color-text-600;
  }
}

.actions-block {
  .add-action-form {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: $space-md;
    align-items: center;
    margin-bottom: $space-lg;
    padding-bottom: $space-md;
    border-bottom: 1px solid $color-primary-100;

    input[type="text"],
    input[type="date"] {
      padding: $space-sm;
      border: 1px solid $color-primary-100;
      border-radius: $border-radius-sm;
      font-size: $font-size-base;
      font-family: $font-body;
      color: $color-text-600;
      background-color: $color-background;
      &:focus {
        outline: none;
        border-color: $color-primary-500;
        box-shadow: 0 0 0 3px rgba($color-primary-500, 0.2);
      }
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background-color: lighten($color-background, 2%);
      }
    }
    input[type="text"]::placeholder,
    input[type="date"]::placeholder {
      color: $color-text-300;
    }

    input[type="text"]:first-child {
      min-width: 150px;
    }

    .base-button {
      padding: $space-sm $space-md;
    }
  }

  .actions-list {
    // Styles pour TransitionGroup
    .list-enter-active,
    .list-leave-active {
      transition: all $transition-duration-medium ease;
    }
    .list-enter-from,
    .list-leave-to {
      opacity: 0;
      transform: translateX(30px);
    }
    .list-leave-active {
      position: absolute; /* Permet aux éléments suivants de glisser */
    }

    .action-item {
      /* Styles par défaut pour l'item, actifs en mode normal ET édition */
      display: flex; // Défini par défaut (mode normal) et écrasé en mode édition (grid)
      align-items: center;
      padding: $space-sm 0; // Padding en mode normal
      border-bottom: 1px solid $color-background;
      font-size: $font-size-base;
      color: $color-text-600;
      transition: background-color $transition-duration-short ease-in-out,
        padding $transition-duration-short ease-in-out,
        border-radius $transition-duration-short ease-in-out;

      &:last-child {
        border-bottom: none;
      }

      // Styles spécifiques quand l'action est en cours d'édition
      &.is-editing {
        background-color: $color-primary-100;
        padding: $space-md $space-sm; // Plus de padding en mode édition
        border-radius: $border-radius-sm;
        display: grid; // Passage en mode grid
        grid-template-columns: auto 1fr auto auto auto auto;
        gap: $space-sm;
        align-items: center;
      }
      &.is-loading {
        opacity: 0.8;
        pointer-events: none;
      }

      // Icône de statut (toujours visible, style change avec le statut)
      .action-status-icon {
        flex-shrink: 0;
        cursor: pointer;

        .feather-icon {
          transition: color $transition-duration-short ease-in-out;
          display: block;

          &.status-open {
            color: $color-text-300;
          }
          &.status-in-progress {
            color: $color-warning;
            animation: pulse 2s infinite;
          }
          &.status-done {
            color: $color-success;
          }
        }
        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
          100% {
            opacity: 1;
          }
        }
      }

      /* -- CONTRÔLE DE LA VISIBILITÉ DES ÉLÉMENTS EN MODE NORMAL vs ÉDITION -- */

      // Éléments visibles SEULEMENT en mode NORMAL: Description, Meta, Icones Modifier/Supprimer
      // On les cache quand le parent .action-item a la classe .is-editing

      .action-description,
      .action-meta,
      .action-item-actions .action-icon {
        .action-item.is-editing & {
          // Cache ces éléments quand le parent est en mode édition
          display: flex;
        }
      }

      // Éléments visibles SEULEMENT en mode ÉDITION: Inputs, Boutons OK/Annuler
      // On les cache par défaut et les affiche quand le parent .action-item a la classe .is-editing

      input.edit-input,
      .action-item-actions .base-button {
        display: flex; // Caché par défaut (mode normal)

        .action-item.is-editing & {
          // Affiche ces éléments quand le parent est en mode édition
          /*
             Utiliser !important ici pour s'assurer que ça prend le dessus
             sur tout autre display: none potentiel.
             Une fois que ça marche, on peut essayer de retirer !important
             et s'assurer que la spécificité est suffisante.
             */
          display: initial !important; // Utilise initial pour revenir au comportement par défaut de l'élément (block, flex, etc.)
        }
      }
      /* -- FIN CONTRÔLE VISIBILITÉ -- */

      // Styles spécifiques pour la description (visble en mode normal)
      .action-description {
        flex-grow: 1;
        &.status-done {
          text-decoration: line-through;
          color: $color-text-300;
        }
      }

      // Styles spécifiques pour les métadonnées (Assigné, Date) (visble en mode normal)
      .action-meta {
        flex-shrink: 0;
        display: flex; /* Reste flex à l'intérieur */
        align-items: center;
        font-size: $font-size-sm;
        color: $color-text-300;
        white-space: nowrap;
        span {
          display: flex;
          align-items: center;
          margin-left: $space-md;
          .feather-icon {
            margin-right: $space-xs;
            color: $color-text-300;
          }
        }
        span:first-child {
          margin-left: 0;
        }
      }

      // Styles spécifiques pour les champs d'édition inline (visibles en mode édition)
      input.edit-input {
        width: 100%; /* Occupe la largeur de sa colonne dans la grille */
        padding: $space-xs $space-sm;
        border: 1px solid $color-primary-100;
        border-radius: $border-radius-sm;
        font-size: $font-size-base;
        font-family: $font-body;
        color: $color-text-600;
        background-color: $color-background;
        &:focus {
          outline: none;
          border-color: $color-primary-500;
          box-shadow: 0 0 0 3px rgba($color-primary-500, 0.2);
        }
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background-color: lighten($color-background, 2%);
        }
      }
      input.edit-input[type="date"],
      input.edit-input[type="text"]:not(:first-of-type) {
        min-width: 80px;
      }

      // Styles pour le conteneur des boutons d'action (modifier/supprimer ou ok/annuler)
      .action-item-actions {
        display: flex; /* Toujours flex */
        align-items: center;
        gap: $space-sm;
        flex-shrink: 0;

        // Icônes Modifier/Supprimer (visibles en mode normal)
        .action-icon {
          cursor: pointer;
          color: $color-text-300;
          transition: color $transition-duration-short ease-in-out,
            transform $transition-duration-short ease-in-out;
          display: block;
          &:hover {
            transform: scale(1.1);
            color: $color-primary-500;
          }
          &--edit:hover {
            color: $color-accent-500;
          }
          &--delete:hover {
            color: $color-error;
          }
        }

        // Boutons OK/Annuler (visibles en mode édition)
        .base-button {
          padding: $space-xs $space-sm;
          font-size: $font-size-sm;
          .feather-icon {
            margin-right: $space-xs / 2;
          }
        }
      }
    }
  }

  .no-actions-message {
    text-align: center;
    color: $color-text-300;
    padding: $space-lg;
  }
  .error-message {
    color: $color-error;
    text-align: center;
    margin-top: $space-md;
  }
}

.back-button-block {
  text-align: center;
  margin-top: $space-xl;
  background: none;
  box-shadow: none;
  padding: 0;
}
</style>
